import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTickets } from '../../../contexts/TicketContext';
import { useAuth } from '../../../contexts/AuthContext';
import {
    Clock,
    CheckCircle2,
    TrendingUp,
    TrendingDown,
    AlertTriangle,
    MessageSquare,
    Users,
    Activity,
    Info,
    Star,
    Shield,
    BarChart3,
    Map
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from '@/components/ui/badge';
import { cn } from "@/lib/utils";

// Helper for formatting duration
const formatDuration = (ms) => {
    if (!ms) return '-';
    const totalMinutes = Math.floor(ms / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
};

// Helper for generic tooltip card
const MetricCard = ({ title, value, subtext, icon: Icon, color, bg, tooltip, trend }) => (
    <Card className="bg-black/40 border-white/10 backdrop-blur-md hover:bg-white/5 transition-all group h-full">
        <CardContent className="p-6 flex flex-col justify-between h-full">
            <div className="flex justify-between items-start">
                <div>
                    <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-400">{title}</p>
                        {tooltip && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Info className="h-4 w-4 text-gray-500 hover:text-white cursor-pointer transition-colors" />
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-gray-900 border-white/10 text-white p-3 text-xs max-w-[200px]">
                                        <p>{tooltip}</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                    </div>
                    <div className="flex items-end gap-2 mt-2">
                        <h3 className="text-2xl font-bold text-white">{value}</h3>
                        {trend && (
                            <div className={cn("flex items-center text-xs mb-1", trend > 0 ? "text-green-400" : "text-red-400")}>
                                {trend > 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
                                {Math.abs(trend)}%
                            </div>
                        )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{subtext}</p>
                </div>
                <div className={`p-3 rounded-xl ${bg} group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className={`w-5 h-5 ${color}`} />
                </div>
            </div>
        </CardContent>
    </Card>
);

export const SectorTab = () => {
    const { tickets } = useTickets();
    const [selectedSector, setSelectedSector] = useState('All');

    // 1. Data Aggregation
    const sectorStats = useMemo(() => {
        const stats = {
            'Suporte': {
                count: 0, resolved: 0, open: 0,
                tmaSum: 0, tmaCount: 0,
                tprSum: 0, tprCount: 0,
                slaOk: 0, slaTotal: 0,
                delayed: 0,
                ratingsSum: 0, ratingsCount: 0,
                cities: {},
                trendLastWeek: 0
            },
            'Moderação': { count: 0, resolved: 0, open: 0, tmaSum: 0, tmaCount: 0, tprSum: 0, tprCount: 0, slaOk: 0, slaTotal: 0, delayed: 0, ratingsSum: 0, ratingsCount: 0, cities: {}, trendLastWeek: 0 },
            'Financeiro': { count: 0, resolved: 0, open: 0, tmaSum: 0, tmaCount: 0, tprSum: 0, tprCount: 0, slaOk: 0, slaTotal: 0, delayed: 0, ratingsSum: 0, ratingsCount: 0, cities: {}, trendLastWeek: 0 },
            'Técnico': { count: 0, resolved: 0, open: 0, tmaSum: 0, tmaCount: 0, tprSum: 0, tprCount: 0, slaOk: 0, slaTotal: 0, delayed: 0, ratingsSum: 0, ratingsCount: 0, cities: {}, trendLastWeek: 0 },
            'Outros': { count: 0, resolved: 0, open: 0, tmaSum: 0, tmaCount: 0, tprSum: 0, tprCount: 0, slaOk: 0, slaTotal: 0, delayed: 0, ratingsSum: 0, ratingsCount: 0, cities: {}, trendLastWeek: 0 },
        };

        const SLA_LIMIT_MS = 24 * 60 * 60 * 1000; // 24h SLA for resolution example
        const DELAY_LIMIT_MS = 48 * 60 * 60 * 1000; // 48h for "Atraso"

        const now = new Date();
        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

        tickets.forEach(t => {
            // Determine Sector
            let sector = 'Outros';
            const cat = t.category ? t.category.toLowerCase() : '';
            if (cat.includes('bug') || cat.includes('erro') || cat.includes('programacao') || cat.includes('falha')) sector = 'Técnico';
            else if (cat.includes('dúvida') || cat.includes('suporte') || cat.includes('ajuda')) sector = 'Suporte';
            else if (cat.includes('denúncia') || cat.includes('ban') || cat.includes('hack') || cat.includes('report') || cat.includes('contestar')) sector = 'Moderação';
            else if (cat.includes('vip') || cat.includes('doação') || cat.includes('compra') || cat.includes('pix') || cat.includes('cupom')) sector = 'Financeiro';

            const s = stats[sector];
            s.count++;

            // Trend (Volume last week)
            if (new Date(t.createdAt) > oneWeekAgo) {
                s.trendLastWeek++;
            }

            // Status Counts
            if (t.status === 'resolvido' || t.status === 'fechado') {
                s.resolved++;
                // TMA - Resolution Time
                const duration = new Date(t.lastUpdate) - new Date(t.createdAt);
                if (duration > 0) {
                    s.tmaSum += duration;
                    s.tmaCount++;
                    // SLA Check
                    s.slaTotal++;
                    if (duration <= SLA_LIMIT_MS) s.slaOk++;
                }

            } else {
                s.open++;
                // Delayed Check (Open longer than 48h)
                const openTime = now - new Date(t.createdAt);
                if (openTime > DELAY_LIMIT_MS) s.delayed++;
            }

            // TPR - First Response
            if (t.responses && t.responses.length > 0) {
                // Find first staff response
                const sorted = [...t.responses].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                const firstStaff = sorted.find(r => r && r.isStaff);
                if (firstStaff) {
                    const time = new Date(firstStaff.createdAt) - new Date(t.createdAt);
                    if (time > 0) {
                        s.tprSum += time;
                        s.tprCount++;
                    }
                }
            }

            // Ratings
            if (t.feedback && t.feedback.score) {
                s.ratingsSum += Number(t.feedback.score);
                s.ratingsCount++;
            }

            // Cities
            // Try to find city in t.extraInfo, t.description or specific fields if available
            // Mocking for now as typical ticket structure wasn't fully deep dived for city fields
            // Assuming we might have `t.city` or similar from ticket flow
            const city = t.city || 'Desconhecida';
            if (!s.cities[city]) s.cities[city] = 0;
            s.cities[city]++;
        });

        // Calculate Averages & Trends
        Object.keys(stats).forEach(key => {
            const s = stats[key];
            s.avgTma = s.tmaCount ? s.tmaSum / s.tmaCount : 0;
            s.avgTpr = s.tprCount ? s.tprSum / s.tprCount : 0;
            s.slaPercent = s.slaTotal ? Math.round((s.slaOk / s.slaTotal) * 100) : 100; // Default 100 if no closed tickets
            s.rating = s.ratingsCount ? (s.ratingsSum / s.ratingsCount).toFixed(1) : 0;
            s.resolutionRate = s.count ? Math.round((s.resolved / s.count) * 100) : 0;

            // Mock Trend % (vs previous week - simplistic logic)
            // Assuming uniform distribution, prev week would be similar. 
            // We just calculate a dummy variation for UI demo purposes based on random or subset
            s.volumeTrend = Math.floor(Math.random() * 20) - 10; // Random -10 to +10%
        });

        return stats;
    }, [tickets]);

    // Handle Sector Selection
    const activeStats = selectedSector === 'All'
        ? Object.values(sectorStats).reduce((acc, curr) => {
            // Aggregate all for "Global View" logic if needed, but usually we compare sectors.
            // For this UI, let's keep it simple: Show cards for SPECIFIC sector when selected, 
            // or a summary if 'All' (maybe biggest sector?)
            // Actually, let's default to 'Suporte' as main if 'All' is confused, or show meaningful aggregate.
            // Let's implement switching:
            return acc;
        }, {})
        : sectorStats[selectedSector];

    // For 'All' view we might want a comparison grid.
    // Let's stick to a dropdown selector for detailed view or just show grid of sectors.
    // The user wants "Ranking de Cidades por Setor", etc.
    // Let's provide tabs for sectors inside this tab or a simple selector.

    // Defaulting to "Suporte" if All to show the detailed dashboard structure first.
    const currentData = selectedSector === 'All' ? sectorStats['Suporte'] : sectorStats[selectedSector];
    const sectorName = selectedSector === 'All' ? 'Suporte (Padrão)' : selectedSector;

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Sector Selector */}
            <div className="flex flex-wrap gap-2 pb-4 border-b border-white/10">
                {Object.keys(sectorStats).map(sector => (
                    <Button
                        key={sector}
                        variant="ghost"
                        onClick={() => setSelectedSector(sector)}
                        className={cn(
                            "rounded-full px-6",
                            selectedSector === sector
                                ? "bg-indigo-500/20 text-indigo-300 border border-indigo-500/30"
                                : "bg-black/20 text-gray-500 hover:bg-black/40 hover:text-gray-300"
                        )}
                    >
                        {sector}
                    </Button>
                ))}
            </div>

            {/* Title */}
            <div className="flex items-center gap-2 mb-2">
                <Users className="h-5 w-5 text-indigo-400" />
                <span className="text-lg font-bold text-white uppercase tracking-wider">
                    Análise: {sectorName}
                </span>
            </div>

            {/* --- CORE METRICS ROW --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* TMA */}
                <MetricCard
                    title="TMA (Tempo Médio)"
                    value={formatDuration(currentData.avgTma)}
                    subtext="Tempo para fechar ticket"
                    icon={Clock}
                    color="text-blue-400"
                    bg="bg-blue-500/10"
                    tooltip="Tempo Médio de Atendimento: Quanto tempo, em média, a equipe leva para resolver e fechar um ticket neste setor."
                />

                {/* TPR */}
                <MetricCard
                    title="TPR (1ª Resposta)"
                    value={formatDuration(currentData.avgTpr)}
                    subtext="Agilidade inicial"
                    icon={Activity}
                    color="text-purple-400"
                    bg="bg-purple-500/10"
                    tooltip="Tempo de Primeira Resposta: Quanto tempo o cliente espera até receber o primeiro retorno da equipe."
                />

                {/* SLA */}
                <MetricCard
                    title="Dentro do SLA"
                    value={`${currentData.slaPercent}%`}
                    subtext={`${currentData.slaOk} / ${currentData.slaTotal} tickets`}
                    icon={CheckCircle2}
                    color={currentData.slaPercent >= 90 ? "text-emerald-400" : "text-yellow-400"}
                    bg={currentData.slaPercent >= 90 ? "bg-emerald-500/10" : "bg-yellow-500/10"}
                    tooltip="Porcentagem de tickets resolvidos dentro do prazo estipulado (ex: 24h)."
                />

                {/* CSAT */}
                <MetricCard
                    title="CSAT (Satisfação)"
                    value={currentData.rating || '-'}
                    subtext={`${currentData.ratingsCount} avaliações`}
                    icon={Star}
                    color="text-yellow-400"
                    bg="bg-yellow-500/10"
                    tooltip="Índice de Satisfação do Cliente: Média das notas (1-5) atribuídas pelos usuários ao final do atendimento."
                />
            </div>

            {/* --- SECONDARY METRICS ROW --- */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Backlog */}
                <MetricCard
                    title="Backlog / Pendentes"
                    value={currentData.open}
                    subtext="Aguardando resolução"
                    icon={AlertTriangle}
                    color="text-orange-400"
                    bg="bg-orange-500/10"
                    tooltip="Total de tickets que ainda não foram resolvidos. Um número alto indica acúmulo de demandas."
                />

                {/* Delayed */}
                <MetricCard
                    title="Tickets em Atraso"
                    value={currentData.delayed}
                    subtext="> 48h sem resolução"
                    icon={TrendingDown}
                    color="text-red-500"
                    bg="bg-red-500/10"
                    tooltip="Contador de tickets que já ultrapassaram o limite de tempo aceitável (ex: 48h) e precisam de atenção imediata."
                />

                {/* Resolution Rate */}
                <Card className="bg-black/40 border-white/10 backdrop-blur-md">
                    <CardContent className="p-6 flex flex-col justify-center h-full">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-gray-400">Taxa de Resolução</span>
                            <span className="text-xl font-bold text-white">{currentData.resolutionRate}%</span>
                        </div>
                        <Progress value={currentData.resolutionRate} className="h-2 bg-white/5" indicatorClassName="bg-indigo-500" />
                        <p className="text-xs text-center text-gray-500 mt-3">
                            {currentData.resolved} resolvidos de {currentData.count} totais
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* --- CITY RANKING & INSIGHTS --- */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* City Ranking */}
                <Card className="bg-black/40 border-white/10 backdrop-blur-md">
                    <CardHeader>
                        <CardTitle className="text-white text-lg flex items-center gap-2">
                            <Map className="h-5 w-5 text-indigo-400" />
                            Ranking de Cidades ({sectorName})
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {Object.entries(currentData.cities)
                                .sort(([, a], [, b]) => b - a)
                                .slice(0, 5) // Top 5
                                .map(([city, count], index) => {
                                    const percentage = Math.round((count / currentData.count) * 100);
                                    return (
                                        <div key={city} className="relative">
                                            <div className="flex justify-between items-center z-10 relative mb-1">
                                                <div className="flex items-center gap-2">
                                                    <Badge variant="outline" className="bg-white/5 border-white/10 text-gray-400 w-6 h-6 flex items-center justify-center p-0">
                                                        {index + 1}
                                                    </Badge>
                                                    <span className="text-sm font-medium text-white">{city}</span>
                                                </div>
                                                <span className="text-xs text-gray-400 font-bold">{count} tickets ({percentage}%)</span>
                                            </div>
                                            <div className="w-full bg-white/5 h-1.5 rounded-full overflow-hidden">
                                                <div className="h-full bg-indigo-500/50" style={{ width: `${percentage}%` }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            {Object.keys(currentData.cities).length === 0 && (
                                <div className="text-center text-gray-500 py-8">
                                    Nenhum dado de cidade disponível para este setor.
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Performance Comparison (City A vs City B - Mockup/Placeholder logic based on available data) */}
                <Card className="bg-gradient-to-br from-indigo-900/10 to-purple-900/10 border-indigo-500/20 backdrop-blur-md">
                    <CardHeader>
                        <CardTitle className="text-white text-lg flex items-center gap-2">
                            <BarChart3 className="h-5 w-5 text-indigo-400" />
                            Comparativo de Performance
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col items-center justify-center text-center h-[200px]">
                        <div className="p-4 bg-indigo-500/20 rounded-full mb-4">
                            <Activity className="h-8 w-8 text-indigo-400" />
                        </div>
                        <h4 className="text-white font-bold mb-2">Cidade com Maior Demanda</h4>
                        <p className="text-gray-400 text-sm max-w-xs">
                            A cidade <strong>{Object.keys(currentData.cities).sort((a, b) => currentData.cities[b] - currentData.cities[a])[0] || 'N/A'}</strong> representa a maior parte do volume de tickets neste setor.
                        </p>
                        <p className="text-xs text-indigo-300 mt-4">
                            Recomendação: Alocar mais staff experiente para esta região.
                        </p>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default SectorTab;
